// Section 17: resolve a priority order into ranked groups of people. Each person is placed in
// the first listed group that contains them; the catch-all `everyone-else` takes whoever no
// other listed group claims, at its own position (so "everyone else, Idle adults" puts idle
// adults last, as the Section 18 default order intends). People in no listed group come last.
// Ties inside a group: the weakest first (lowest strength, then id).
//
// Warmth is resolved per household (beds, not people): a household sits in the tier of its
// highest-ranked member and its members stay together, so every member gets the same share.
import type { HouseholdId, Person, PersonId, PriorityGroupId, ReadContext } from '../../core/types';

const CATCH_ALL: PriorityGroupId = 'everyone-else';

/** Tier index (position in `order`) of every person; `order.length` for the unlisted rest. */
export function placeInOrder(
  ctx: ReadContext,
  order: readonly PriorityGroupId[],
  people: readonly Person[],
): Map<PersonId, number> {
  const known = new Set(people.map((p) => p.id));
  const placed = new Map<PersonId, number>();
  order.forEach((groupId, index) => {
    if (groupId === CATCH_ALL) return;
    for (const id of ctx.registry.priorityGroups[groupId].members(ctx)) {
      if (known.has(id) && !placed.has(id)) placed.set(id, index);
    }
  });
  const catchAll = order.indexOf(CATCH_ALL);
  const rest = catchAll >= 0 ? catchAll : order.length;
  for (const p of people) if (!placed.has(p.id)) placed.set(p.id, rest);
  return placed;
}

function weakestFirst(people: readonly Person[]): (a: PersonId, b: PersonId) => number {
  const strength = new Map(people.map((p) => [p.id, p.strength]));
  return (a, b) => (strength.get(a) ?? 0) - (strength.get(b) ?? 0) || (a < b ? -1 : 1);
}

/** Person-level ranking (food, water). Groups in priority order, members weakest first. */
export function rankGroups(
  ctx: ReadContext,
  order: readonly PriorityGroupId[],
  people: readonly Person[],
): PersonId[][] {
  const tiers = placeInOrder(ctx, order, people);
  const buckets: PersonId[][] = Array.from({ length: order.length + 1 }, () => []);
  for (const p of people) buckets[tiers.get(p.id) ?? order.length]?.push(p.id);
  const cmp = weakestFirst(people);
  return buckets.filter((b) => b.length > 0).map((b) => b.sort(cmp));
}

/** Household-level ranking (warmth): tiers of whole households, the weakest household first. */
export function rankHouseholds(
  ctx: ReadContext,
  order: readonly PriorityGroupId[],
  people: readonly Person[],
): PersonId[][] {
  const tiers = placeInOrder(ctx, order, people);
  const members = new Map<HouseholdId, Person[]>();
  for (const p of people) {
    const list = members.get(p.householdId);
    if (list === undefined) members.set(p.householdId, [p]);
    else list.push(p);
  }
  const cmp = weakestFirst(people);
  const buckets: { weakest: number; id: HouseholdId; ids: PersonId[] }[][] = Array.from(
    { length: order.length + 1 },
    () => [],
  );
  for (const [id, list] of [...members.entries()].sort(([a], [b]) => (a < b ? -1 : 1))) {
    const tier = Math.min(...list.map((p) => tiers.get(p.id) ?? order.length));
    buckets[tier]?.push({
      weakest: Math.min(...list.map((p) => p.strength)),
      id,
      ids: list.map((p) => p.id).sort(cmp),
    });
  }
  return buckets
    .filter((b) => b.length > 0)
    .map((b) =>
      b.sort((x, y) => x.weakest - y.weakest || (x.id < y.id ? -1 : 1)).flatMap((h) => h.ids),
    );
}
